// ─── IMIGONGO PATTERN ─────────────────────────────────────────────────────────
// Tileable SVG of nested diamond outlines, inspired by traditional Rwandan
// Imigongo geometric borders (rows of concentric lozenges). Pure thin strokes,
// no fills/curves, kept at low opacity so it reads as a faint repeating texture.
function imigongoPattern(color, opacity, size = 64) {
  const c = encodeURIComponent(color);
  const h = size / 2;
  const o2 = size * 0.25;     // inner diamond inset
  const paths = [
    // outer diamond touches edge mid-points so it forms a continuous
    // interlocking trellis when tiled
    `M${h} 0 L${size} ${h} L${h} ${size} L0 ${h} Z`,
    `M${h} ${o2} L${size - o2} ${h} L${h} ${size - o2} L${o2} ${h} Z`,
  ];
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${size}' height='${size}' viewBox='0 0 ${size} ${size}'>`
    + `<g fill='none' stroke='${c}' stroke-opacity='${opacity}' stroke-width='1.25'>`
    + paths.map(d => `<path d='${d}'/>`).join('')
    + `</g></svg>`;
  return `url("data:image/svg+xml,${svg.replace(/'/g, '%27').replace(/#/g, '%23')}")`;
}

// ─── IMIGONGO CONCENTRIC-SQUARE PATTERN (alt) ──────────────────────────────────
// Tileable SVG of nested square "rings" with a center dot, echoing the classic
// Imigongo concentric/spiral-square mat motif. Pure thin strokes, low opacity.
function imigongoSquares(color, opacity, size = 80) {
  const c = encodeURIComponent(color);
  const step = size * 0.11;
  const rings = [];
  for (let i = 0; i < 4; i++) {
    const inset = size * 0.06 + i * step;
    const s = size - inset * 2;
    rings.push(`<rect x='${inset}' y='${inset}' width='${s}' height='${s}' rx='${s * 0.08}'/>`);
  }
  const h = size / 2;
  const dotR = size * 0.025;
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='${size}' height='${size}' viewBox='0 0 ${size} ${size}'>`
    + `<g fill='none' stroke='${c}' stroke-opacity='${opacity}' stroke-width='1.25'>${rings.join('')}</g>`
    + `<circle cx='${h}' cy='${h}' r='${dotR}' fill='${c}' fill-opacity='${opacity}'/>`
    + `</svg>`;
  return `url("data:image/svg+xml,${svg.replace(/'/g, '%27').replace(/#/g, '%23')}")`;
}

// Light-mode content texture (~6% opacity)
const PATTERN_CONTENT      = imigongoSquares('#04120e', 0.06, 80);
// Dark-mode content texture (~7% opacity)
const PATTERN_CONTENT_DARK = imigongoSquares('#5fe0b8', 0.07, 80);
// Sidebar overlay — more visible (~14% opacity)
const PATTERN_SIDEBAR      = imigongoSquares('#ffffff', 0.14, 80);
// Auth pages (~9% opacity)
const PATTERN_AUTH         = imigongoSquares('#ffffff', 0.09, 80);
// Modal backdrop (~6% opacity)
const PATTERN_MODAL        = imigongoSquares('#ffffff', 0.06, 80);

export const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,700;9..144,900&family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  :root{
    --ink:#04120e;--ink2:#122b1e;--teal:#0db87e;--teal-hi:#0fd491;--teal-lo:#d0f5e8;--teal-dim:#e8faf3;
    --amber:#f5a623;--amber-lo:#fef3dc;--rose:#e03050;--rose-lo:#fde8ec;--blue:#2563eb;--blue-lo:#dbeafe;
    --surface:#f5f9f7;--card:#fff;--border:#e0ede7;--muted:#4a6b58;--ghost:#8aaa98;
    --fd:'Fraunces',serif;--fb:'DM Sans',sans-serif;--fm:'DM Mono',monospace;
    --r:12px;--r-lg:18px;--sh:0 1px 4px rgba(4,18,14,.06);--sh-md:0 4px 20px rgba(4,18,14,.09);
    --panel-dark:#04120e;

    /* IMIGONGO PATTERN TOKENS */
    --pattern-content:${PATTERN_CONTENT};
    --pattern-content-size:80px;
    --pattern-sidebar:${PATTERN_SIDEBAR};
    --pattern-sidebar-size:80px;
    --pattern-auth:${PATTERN_AUTH};
    --pattern-auth-size:80px;
    --pattern-modal:${PATTERN_MODAL};
    --pattern-modal-size:80px;
    --sidebar-gradient:linear-gradient(165deg,#04120e 0%,#0a2e1c 55%,#04140d 100%);
  }
  /* DARK MODE */
  .dark{
    --ink:#e8f5ee;--ink2:#afd0be;--surface:#0b1f16;--card:#0f2a1c;--border:#1a3d28;--muted:#8abba0;--ghost:#4d7560;
    --teal-dim:#0f2e20;--teal-lo:#0d3320;--amber-lo:#2a1f08;--rose-lo:#2a0d12;--blue-lo:#0d1f3c;
    --sh:0 1px 4px rgba(0,0,0,.25);--sh-md:0 4px 20px rgba(0,0,0,.4);
    --panel-dark:#061a0f;
    --pattern-content:${PATTERN_CONTENT_DARK};
    --sidebar-gradient:linear-gradient(165deg,#03100c 0%,#082519 55%,#03100c 100%);
  }
  html,body,#root{height:100%;font-family:var(--fb);background:var(--surface);color:var(--ink);font-size:14px;overflow:hidden;transition:background .2s,color .2s}
  @keyframes spin{to{transform:rotate(360deg)}}
  @keyframes mo{from{transform:scale(.92);opacity:0}to{transform:scale(1);opacity:1}}
  @keyframes toastin{from{transform:translateY(20px);opacity:0}to{transform:translateY(0);opacity:1}}
  @keyframes fadein{from{opacity:0}to{opacity:1}}

  /* LAYOUT */
  .layout{display:flex;height:100vh}

  /* SIDEBAR */
  .sidebar{width:242px;height:100vh;background-color:var(--panel-dark);background-image:var(--pattern-sidebar),var(--sidebar-gradient);background-repeat:repeat,no-repeat;background-size:var(--pattern-sidebar-size) var(--pattern-sidebar-size),cover;display:flex;flex-direction:column;flex-shrink:0;overflow-y:auto;z-index:200;transition:width .25s cubic-bezier(.4,0,.2,1),transform .3s cubic-bezier(.4,0,.2,1)}
  .sidebar::-webkit-scrollbar{display:none}
  .sb-logo{display:flex;align-items:center;gap:10px;padding:20px 18px 16px;border-bottom:1px solid rgba(255,255,255,.06);margin-bottom:8px;flex-shrink:0;position:relative;overflow:hidden}
  .sb-mark{width:36px;height:36px;background:var(--teal);border-radius:10px;display:flex;align-items:center;justify-content:center;font-size:18px;flex-shrink:0}
  .sb-name{font-family:var(--fd);font-size:14px;font-weight:700;color:#fff;line-height:1.1;transition:opacity .15s,width .15s;white-space:nowrap}
  .sb-ver{font-size:10px;color:rgba(255,255,255,.3);font-family:var(--fm);margin-top:2px;white-space:nowrap;transition:opacity .15s}
  .sb-section{font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:1.4px;color:rgba(255,255,255,.35);padding:12px 18px 5px;white-space:nowrap;overflow:hidden;transition:opacity .15s,height .15s,padding .15s}
  .sb-item{display:flex;align-items:center;gap:10px;padding:9px 16px;cursor:pointer;border:none;background:none;width:100%;text-align:left;font-family:var(--fb);transition:background .15s,padding .15s;border-left:3px solid transparent}
  .sb-item:hover{background:rgba(255,255,255,.06)}
  .sb-item.active{background:rgba(255,255,255,.09);backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);border-left-color:var(--teal-hi);box-shadow:inset 0 0 0 1px rgba(13,184,126,.25),0 0 16px rgba(13,184,126,.22)}
  .sb-icon{width:30px;height:30px;border-radius:8px;display:flex;align-items:center;justify-content:center;flex-shrink:0;background:rgba(255,255,255,.09);border:1px solid rgba(255,255,255,.07)}
  .sb-item.active .sb-icon{background:rgba(13,184,126,.25);border-color:rgba(13,184,126,.35)}
  .sb-lbl{font-size:13px;font-weight:500;color:rgba(255,255,255,.72);flex:1;white-space:nowrap;overflow:hidden;transition:opacity .15s,width .15s}
  .sb-item.active .sb-lbl{color:#fff;font-weight:600}
  .sb-badge{font-size:10px;font-weight:700;background:var(--rose);color:#fff;padding:2px 7px;border-radius:20px;font-family:var(--fm)}
  .sb-badge.am{background:var(--amber)}
  .sb-foot{margin-top:auto;padding:14px 16px;border-top:1px solid rgba(255,255,255,.06);display:flex;align-items:center;gap:10px;flex-shrink:0;overflow:hidden;transition:padding .15s}
  .sb-av{width:34px;height:34px;border-radius:50%;background:linear-gradient(135deg,var(--teal),#086b47);display:flex;align-items:center;justify-content:center;font-size:14px;flex-shrink:0}
  .sb-head{flex:1;min-width:0;overflow:hidden;transition:opacity .15s,width .15s}
  .sb-foot-info{flex:1;min-width:0;overflow:hidden;cursor:pointer;transition:opacity .15s,width .15s}
  .sb-collapse-btn{background:none;border:none;cursor:pointer;width:28px;height:28px;border-radius:8px;display:flex;align-items:center;justify-content:center;color:rgba(255,255,255,.3);transition:background .15s,color .15s;flex-shrink:0;margin-left:auto}
  .sb-collapse-btn:hover{background:rgba(255,255,255,.08);color:rgba(255,255,255,.7)}

  /* SIDEBAR COLLAPSED */
  .sidebar.collapsed{width:62px}
  /* Logo area: stack mark + collapse btn vertically, centred */
  .sidebar.collapsed .sb-logo{flex-direction:column;justify-content:center;align-items:center;padding:10px 0 8px;gap:6px}
  .sidebar.collapsed .sb-head{width:0;height:0;opacity:0;overflow:hidden;pointer-events:none}
  .sidebar.collapsed .sb-collapse-btn{margin-left:0}
  /* Nav text */
  .sidebar.collapsed .sb-name,.sidebar.collapsed .sb-ver,.sidebar.collapsed .sb-lbl,.sidebar.collapsed .sb-badge{opacity:0;width:0;overflow:hidden;pointer-events:none}
  .sidebar.collapsed .sb-section{opacity:0;height:0;padding:0;pointer-events:none}
  .sidebar.collapsed .sb-item{justify-content:center;padding:9px 0;border-left-color:transparent}
  .sidebar.collapsed .sb-item.active{border-left-color:var(--teal)}
  /* Footer: keep row, hide name/email text and settings btn */
  .sidebar.collapsed .sb-foot{justify-content:center;padding:14px 0;gap:0}
  .sidebar.collapsed .sb-foot-info{width:0;opacity:0;overflow:hidden;pointer-events:none}
  .sidebar.collapsed .sb-settings-btn{width:0;opacity:0;overflow:hidden;pointer-events:none}

  /* MAIN + TOPBAR */
  .main{flex:1;display:flex;flex-direction:column;overflow:hidden;min-width:0}
  .topbar{height:60px;background:var(--card);border-bottom:1px solid var(--border);display:flex;align-items:center;padding:0 clamp(16px,3vw,28px);gap:12px;flex-shrink:0;box-shadow:var(--sh)}
  .tb-bc{font-family:var(--fd);font-size:18px;font-weight:700;color:var(--ink);flex:1}
  .tb-search{display:flex;align-items:center;gap:8px;background:var(--surface);border:1.5px solid var(--border);border-radius:var(--r);padding:7px 12px;max-width:240px;width:100%}
  .tb-search:focus-within{border-color:var(--teal)}
  .tb-search input{border:none;background:none;font-family:var(--fb);font-size:13px;color:var(--ink);outline:none;width:100%}
  .tb-btn{width:36px;height:36px;border:1.5px solid var(--border);border-radius:var(--r);background:white;display:flex;align-items:center;justify-content:center;cursor:pointer;position:relative;transition:all .2s;flex-shrink:0}
  .tb-btn:hover{border-color:var(--teal);background:var(--teal-dim)}
  .btn-broadcast{background:var(--teal);color:#fff;border:none;border-radius:var(--r);padding:8px 16px;font-family:var(--fd);font-size:13px;font-weight:700;cursor:pointer;display:flex;align-items:center;gap:6px;white-space:nowrap;transition:all .2s;flex-shrink:0}
  .btn-broadcast:hover{background:var(--teal-hi);box-shadow:0 4px 16px rgba(13,184,126,.35)}
  .content{flex:1;overflow-y:auto;padding:clamp(16px,3vw,28px);scrollbar-width:thin;scrollbar-color:var(--border) transparent;background-color:var(--surface);background-image:var(--pattern-content);background-repeat:repeat;background-size:var(--pattern-content-size) var(--pattern-content-size)}
  .content::-webkit-scrollbar{width:5px}
  .content::-webkit-scrollbar-thumb{background:var(--border);border-radius:3px}

  /* CARDS + STATS */
  .stats-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,200px),1fr));gap:14px;margin-bottom:20px}
  .stat-card{background:var(--card);border-radius:var(--r-lg);padding:clamp(14px,2vw,20px);border:1px solid var(--border);box-shadow:var(--sh);position:relative;overflow:hidden;transition:box-shadow .2s}
  .stat-card:hover{box-shadow:var(--sh-md)}
  .stat-card::after{content:'';position:absolute;top:0;left:0;right:0;height:3px}
  .sc-teal::after{background:var(--teal)}.sc-amber::after{background:var(--amber)}.sc-rose::after{background:var(--rose)}.sc-blue::after{background:var(--blue)}
  .sc-lbl{font-size:10px;font-weight:700;color:var(--ghost);text-transform:uppercase;letter-spacing:.8px;margin-bottom:8px}
  .sc-val{font-family:var(--fd);font-size:clamp(26px,4vw,34px);font-weight:900;color:var(--ink);line-height:1;margin-bottom:4px}
  .sc-sub{font-size:11px;color:var(--ghost)}
  .sc-chg{display:inline-flex;align-items:center;gap:3px;font-size:11px;font-weight:700;padding:2px 8px;border-radius:20px;margin-top:8px}
  .chg-up{background:#d1fae5;color:#065f46}.chg-nt{background:var(--teal-dim);color:var(--teal)}
  .card{background:var(--card);border-radius:var(--r-lg);border:1px solid var(--border);box-shadow:var(--sh);margin-bottom:18px}
  .card-hdr{display:flex;align-items:center;justify-content:space-between;padding:14px 18px;border-bottom:1px solid var(--border);gap:8px;flex-wrap:wrap}
  .card-ttl{font-family:var(--fd);font-size:14px;font-weight:700;color:var(--ink);display:flex;align-items:center;gap:8px}
  .card-ico{width:26px;height:26px;border-radius:8px;display:flex;align-items:center;justify-content:center;font-size:13px;background:var(--teal-dim)}
  .card-body{padding:clamp(14px,2vw,18px)}
  .cg2{display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,340px),1fr));gap:18px;margin-bottom:18px}

  /* CHARTS */
  .bar-chart{display:flex;align-items:flex-end;gap:6px;height:100px}
  .bc-col{flex:1;display:flex;flex-direction:column;align-items:center;gap:4px;height:100%}
  .bc-wrap{flex:1;width:100%;display:flex;align-items:flex-end}
  .bc-bar{width:100%;border-radius:4px 4px 0 0;background:var(--teal-lo);min-height:3px;transition:background .2s}
  .bc-bar:hover,.bc-bar.hi{background:var(--teal)}
  .bc-lbl{font-size:9px;color:var(--ghost);font-family:var(--fm)}
  .chart-row{display:flex;gap:20px;margin-top:12px;padding-top:12px;border-top:1px solid var(--border);flex-wrap:wrap}
  .cr-lbl{font-size:10px;color:var(--ghost);text-transform:uppercase;letter-spacing:.6px}
  .cr-val{font-family:var(--fd);font-size:17px;font-weight:900;color:var(--ink)}
  .spark{display:flex;align-items:flex-end;gap:2px;height:32px;margin-top:8px}
  .sp-bar{flex:1;border-radius:1px 1px 0 0;min-height:2px}

  /* PROGRESS */
  .prog-track{height:6px;background:var(--border);border-radius:3px;overflow:hidden}
  .prog-fill{height:100%;border-radius:3px;background:linear-gradient(90deg,var(--teal),var(--teal-hi))}
  .prog-fill.am{background:linear-gradient(90deg,var(--amber),#f59e0b)}.prog-fill.rs{background:linear-gradient(90deg,var(--rose),#f43f5e)}

  /* BADGES */
  .badge{display:inline-flex;align-items:center;gap:3px;font-size:10px;font-weight:700;padding:3px 8px;border-radius:20px;text-transform:uppercase;letter-spacing:.4px;white-space:nowrap}
  .badge::before{content:'●';font-size:7px}
  .bg-green{background:#d1fae5;color:#065f46}.bg-orange{background:var(--amber-lo);color:#92400e}
  .bg-red{background:var(--rose-lo);color:#9f1239}.bg-blue{background:var(--blue-lo);color:#1e40af}
  .bg-gray{background:#f3f4f6;color:#374151}.bg-teal{background:var(--teal-dim);color:var(--teal)}
  .bg-purple{background:#ede9fe;color:#5b21b6}

  /* TABLE */
  .tbl-wrap{overflow-x:auto;-webkit-overflow-scrolling:touch}
  table{width:100%;border-collapse:collapse;font-size:13px;min-width:500px}
  thead th{font-size:10px;font-weight:700;color:var(--ghost);text-transform:uppercase;letter-spacing:.7px;padding:10px 14px;text-align:left;background:var(--surface);border-bottom:1px solid var(--border);white-space:nowrap}
  tbody td{padding:11px 14px;border-bottom:1px solid var(--border);color:var(--ink2);vertical-align:middle}
  tbody tr:last-child td{border-bottom:none}
  tbody tr:hover{background:var(--teal-dim)}

  /* BUTTONS */
  .btn{display:inline-flex;align-items:center;gap:6px;padding:7px 14px;border-radius:var(--r);font-family:var(--fd);font-size:12px;font-weight:700;cursor:pointer;border:none;transition:all .2s;white-space:nowrap}
  .btn-teal{background:var(--teal);color:#fff}.btn-teal:hover{background:var(--teal-hi);box-shadow:0 4px 14px rgba(13,184,126,.3)}
  .btn-out{background:white;border:1.5px solid var(--border);color:var(--muted)}.btn-out:hover{border-color:var(--teal);color:var(--teal)}
  .btn-sm{padding:5px 10px;font-size:11px}.btn-xs{padding:3px 8px;font-size:10px}
  .btn-danger{background:var(--rose-lo);border:1.5px solid var(--rose);color:var(--rose)}
  .btn:disabled{opacity:.5;cursor:not-allowed;pointer-events:none}

  /* FORMS */
  .f-lbl{font-size:11px;font-weight:700;color:var(--muted);text-transform:uppercase;letter-spacing:.6px;margin-bottom:5px;display:block}
  .f-in{width:100%;padding:9px 12px;border:1.5px solid var(--border);border-radius:var(--r);font-family:var(--fb);font-size:13px;color:var(--ink);outline:none;background:white;transition:border-color .2s}
  .f-in:focus{border-color:var(--teal)}
  .f-ta{width:100%;padding:9px 12px;border:1.5px solid var(--border);border-radius:var(--r);font-family:var(--fb);font-size:13px;color:var(--ink);outline:none;resize:none;background:white;line-height:1.6;transition:border-color .2s}
  .f-ta:focus{border-color:var(--teal)}
  .f-sel{width:100%;padding:9px 12px;border:1.5px solid var(--border);border-radius:var(--r);font-family:var(--fb);font-size:13px;color:var(--ink);outline:none;background:white;cursor:pointer}
  .f-grp{margin-bottom:12px}
  .f-row{display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,180px),1fr));gap:12px;margin-bottom:12px}

  /* MODAL */
  .modal-ov{display:none;position:fixed;inset:0;background-color:rgba(4,18,14,.5);background-image:var(--pattern-modal);background-repeat:repeat;background-size:var(--pattern-modal-size) var(--pattern-modal-size);z-index:1000;align-items:center;justify-content:center;backdrop-filter:blur(4px);padding:16px}
  .modal-ov.open{display:flex}
  .modal{background:var(--card);border-radius:20px;padding:clamp(20px,4vw,28px);width:100%;max-width:480px;max-height:90vh;overflow:auto;resize:both;min-width:320px;min-height:180px;box-shadow:0 24px 80px rgba(4,18,14,.2);animation:mo .25s cubic-bezier(.34,1.3,.64,1)}
  .modal-ttl{font-family:var(--fd);font-size:17px;font-weight:900;margin-bottom:18px;display:flex;justify-content:space-between;align-items:center}
  .modal-close{background:none;border:none;font-size:20px;cursor:pointer;color:var(--ghost);line-height:1;padding:0}

  /* IMIGONGO BACKGROUND UTILITY — for auth pages, landing page or any
     full-screen wrapper that needs the pattern layered over its own
     background without altering layout. */
  .imigongo-bg{position:relative}
  .imigongo-bg::before{content:'';position:absolute;inset:0;background-image:var(--pattern-auth);background-repeat:repeat;background-size:var(--pattern-auth-size) var(--pattern-auth-size);pointer-events:none;z-index:0}
  .imigongo-bg>*{position:relative;z-index:1}

  /* MISC */
  .sec-hd{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:16px;gap:12px;flex-wrap:wrap}
  .sec-ttl{font-family:var(--fd);font-size:clamp(16px,2.5vw,20px);font-weight:900}
  .sec-sub{font-size:12px;color:var(--ghost);margin-top:2px}
  .side-panel{display:flex;gap:20px;align-items:flex-start;flex-wrap:wrap}
  .side-main{flex:1;min-width:0}
  .side-aside{width:260px;flex-shrink:0}
  .filter-bar{display:flex;gap:8px;margin-bottom:16px;flex-wrap:wrap;align-items:center}
  .act-item{display:flex;gap:10px;padding:9px 0;border-bottom:1px solid var(--border)}
  .act-item:last-child{border-bottom:none}
  .upl-zone{border:2px dashed var(--border);border-radius:var(--r);padding:clamp(16px,3vw,28px);text-align:center;cursor:pointer;background:var(--surface);transition:all .2s}
  .upl-zone:hover{border-color:var(--teal);background:var(--teal-dim)}
  .tc{font-family:var(--fm);font-size:11px;font-weight:700;background:var(--teal-dim);color:var(--teal);padding:2px 7px;border-radius:6px;white-space:nowrap}
  .av{display:flex;align-items:center;justify-content:center;border-radius:50%;background:var(--teal-dim);color:var(--teal);font-weight:800;flex-shrink:0}
  .overlay{display:none;position:fixed;inset:0;background:rgba(4,18,14,.5);z-index:150;backdrop-filter:blur(2px)}
  .overlay.show{display:block}
  .tb-menu{display:none;width:36px;height:36px;border:1.5px solid var(--border);border-radius:var(--r);background:white;align-items:center;justify-content:center;cursor:pointer;flex-shrink:0}
  .toast{position:fixed;bottom:24px;right:24px;z-index:2000;background:var(--panel-dark);color:white;border-radius:12px;padding:12px 20px;font-size:13px;font-family:var(--fd);font-weight:600;box-shadow:0 8px 32px rgba(4,18,14,.3);animation:toastin .3s cubic-bezier(.34,1.3,.64,1);display:flex;align-items:center;gap:10px;max-width:340px}
  .toast.error{background:var(--rose)}.toast.ok{background:#065f46}
  .notif-panel{position:absolute;top:48px;right:0;width:320px;background:var(--card);border:1px solid var(--border);border-radius:var(--r-lg);box-shadow:var(--sh-md);z-index:500;animation:mo .2s ease}
  .empty-state{text-align:center;padding:48px 24px;color:var(--ghost)}
  .loading-state{display:flex;align-items:center;justify-content:center;padding:48px;color:var(--ghost);gap:10px}
  .ann-push{background:var(--panel-dark);border-radius:14px;padding:14px;margin-bottom:10px}
  .push-ttl{font-size:13px;font-weight:700;color:white;margin-bottom:3px}
  .push-body{font-size:11px;color:rgba(255,255,255,.55);line-height:1.4}
  .banner-prev{background:var(--teal-dim);border-radius:10px;padding:10px;border-left:3px solid var(--teal);margin-bottom:10px}
  .funnel-row{margin-bottom:12px}
  .funnel-num{width:20px;height:20px;border-radius:50%;background:var(--teal-dim);color:var(--teal);font-size:10px;font-weight:800;display:flex;align-items:center;justify-content:center;flex-shrink:0}

  /* DARK MODE OVERRIDES for form inputs, table, cards, topbar, sidebar */
  .dark .f-in,.dark .f-ta,.dark .f-sel{background:var(--card);color:var(--ink);border-color:var(--border)}
  .dark .f-in:focus,.dark .f-ta:focus{border-color:var(--teal)}
  .dark .card{background:var(--card);border-color:var(--border)}
  .dark .topbar{background:var(--card);border-color:var(--border)}
  .dark thead th{background:var(--surface)}
  .dark tbody tr:hover{background:var(--teal-dim)}
  .dark .btn-out{background:var(--card);border-color:var(--border);color:var(--muted)}
  .dark .btn-out:hover{border-color:var(--teal);color:var(--teal)}
  .dark .tb-search{background:var(--surface);border-color:var(--border)}
  .dark .tb-search input{color:var(--ink)}
  .dark .tb-btn{background:rgba(255,255,255,.07);border-color:rgba(255,255,255,.12);color:var(--ink)}
  .dark .tb-btn:hover{background:rgba(255,255,255,.12);border-color:var(--teal)}
  .dark .modal{background:var(--card)}
  .dark .notif-panel{background:var(--card);border-color:var(--border)}
  .dark .upl-zone{background:var(--surface);border-color:var(--border)}
  .dark .upl-zone:hover{background:var(--teal-dim);border-color:var(--teal)}

  /* AVATAR UPLOAD */
  .av-wrap{position:relative;display:inline-block;cursor:pointer}
  .av-wrap img{width:64px;height:64px;border-radius:16px;object-fit:cover;display:block}
  .av-overlay{position:absolute;inset:0;border-radius:16px;background:rgba(4,18,14,.55);display:flex;align-items:center;justify-content:center;opacity:0;transition:opacity .2s}
  .av-wrap:hover .av-overlay{opacity:1}
  .av-uploading .av-overlay{opacity:1}

  /* THEME TOGGLE */
  .theme-pill{display:flex;background:var(--surface);border:1.5px solid var(--border);border-radius:10px;overflow:hidden;padding:3px;gap:2px}
  .theme-opt{padding:6px 14px;border-radius:8px;border:none;cursor:pointer;font-family:var(--fb);font-size:12px;font-weight:500;color:var(--muted);background:transparent;display:flex;align-items:center;gap:5px;transition:all .15s;white-space:nowrap}
  .theme-opt.active{background:var(--card);color:var(--ink);font-weight:700;box-shadow:0 1px 4px rgba(4,18,14,.08)}
  .dark .theme-opt.active{box-shadow:0 1px 4px rgba(0,0,0,.3)}

  /* STAT CARD CLICKABLE */
  .stat-card.clickable{cursor:pointer;user-select:none}
  .stat-card.clickable:hover{box-shadow:var(--sh-md);transform:translateY(-1px)}
  .stat-card.clickable.active{outline:2px solid var(--teal);outline-offset:2px}

  /* SECTION FILTER TABS */
  .sec-tabs{display:flex;gap:4px;background:var(--surface);border:1.5px solid var(--border);border-radius:var(--r);padding:3px}
  .sec-tab{padding:5px 14px;border-radius:8px;border:none;cursor:pointer;font-family:var(--fb);font-size:12px;font-weight:500;color:var(--muted);background:transparent;transition:all .15s;white-space:nowrap}
  .sec-tab.active{background:var(--card);color:var(--teal);font-weight:700;box-shadow:0 1px 3px rgba(4,18,14,.08)}
  .dark .sec-tab.active{box-shadow:0 1px 3px rgba(0,0,0,.25)}

  /* RESOURCE CARDS GRID */
  .res-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(min(100%,220px),1fr));gap:12px;padding:16px}
  .res-card{background:var(--surface);border:1px solid var(--border);border-radius:var(--r);padding:14px;display:flex;flex-direction:column;gap:8px;transition:box-shadow .2s}
  .res-card:hover{box-shadow:var(--sh-md)}

  /* RESPONSIVE */
  @media(max-width:768px){
    .sidebar{position:fixed;top:0;left:0;height:100vh;transform:translateX(-100%)}
    .sidebar.open{transform:translateX(0)}
    .tb-menu{display:flex!important}
    .tb-search{display:none}
    .side-aside{width:100%}
  }
  @media(max-width:480px){.stats-grid{grid-template-columns:1fr 1fr}}
`;
